import React, { useMemo } from 'react';
import { Form, Field } from 'react-final-form';
import * as T from 'prop-types';
import createDecorator from 'final-form-focus';
import Disclaimer from '../../commons/Disclaimer';
import { useAutoSaveDraft } from '../../commons/draftHooks';
import { PageErrorComponent } from '../../commons/PageErrorComponent';
import { getTermsOfParticipationByCountry } from '../../commons/terms-of-participation/TermsOfParticipationUtils';
import { SignToPViaDigitalSignatureButton } from '../digitalSignature/SignToPViaDigitalSignatureButton';
import { ErrorMessage } from '../../commons/form-fields/ErrorMessage';
import { SIGN_STATUS } from '../../commons/digitalSignature/DigitalSignatureStatus';
import GenericCheckboxInputField from '../../commons/form-fields/GenericCheckboxInputField';
import { isMonitorAndRecordConsentRequired } from '../../commons/CountryUtils';

const _validateSignField = (value) =>
    value === SIGN_STATUS.SIGNED
        ? undefined
        : 'Please sign the Terms of Participation';

const SignToPViaDigitalSignatureField = (props) => (
    <Field name={props.fieldName} validate={_validateSignField}>
        {({ input: { onChange }, meta }) => (
            <div>
                <div>
                    <span className="px-2.5 w-2.5" />
                    <span className="display: inline-block">
                        <SignToPViaDigitalSignatureButton
                            onStatusChange={onChange}
                        />
                    </span>
                </div>
                <ErrorMessage meta={meta} />
            </div>
        )}
    </Field>
);

SignToPViaDigitalSignatureField.propTypes = {
    fieldName: T.string,
};

const TermsOfParticipationFields = (props) => {
    const { country } = props;
    const termsOfParticipation = getTermsOfParticipationByCountry(country);
    const monitorAndRecordConsentRequired =
        isMonitorAndRecordConsentRequired(country);
    return (
        <div className="mt-2 max-w-6xl w-full mx-auto py-4">
            <Disclaimer>
                <p>Terms of Participation for Industry Consultants</p>
            </Disclaimer>
            <div className="w-full mt-4 overflow-hidden max-w-4xl mx-auto bg-white px-6 py-4 md:mt-6 md:mb-6 rounded shadow-md text-sm">
                {termsOfParticipation.element}
            </div>
            <Field
                name="application.terms.version"
                initialValue={termsOfParticipation.version}
                render={() => <div />}
            />
            <Field
                name="application.terms.country"
                initialValue={props.country}
                render={() => <div />}
            />
            <div className="w-full my-4 py-4 overflow-hidden bg-white rounded">
                <SignToPViaDigitalSignatureField
                    fieldName={
                        'application.terms.termsOfParticipationDigitallySigned'
                    }
                />
            </div>

            {/* Field should be hidden for US experts and false value should be stored in the DB - Prop#339126#103 */}
            <div
                style={{
                    display: monitorAndRecordConsentRequired ? 'block' : 'none',
                }}
            >
                <GenericCheckboxInputField
                    name="application.terms.monitorAndRecordConsent"
                    label={
                        'I consent to the live recording and/or monitoring of any Consultation that I participate in (and the associated processing of my personal data) by DESCO for the purpose of compliance with law and regulations, in DESCO’s sole discretion. I understand that I have the right to withdraw my consent at any time but that this will not invalidate the processing carried out in reliance on my consent prior to my withdrawal.'
                    }
                    errorMsg={
                        'Please confirm that you provide the above consent.'
                    }
                    defaultValue={
                        monitorAndRecordConsentRequired ? undefined : false
                    }
                    required={monitorAndRecordConsentRequired}
                />
            </div>
        </div>
    );
};

TermsOfParticipationFields.propTypes = {
    country: T.string,
};

const TermsOfParticipationOnboardingPage = (props) => {
    // return same decorator for subsequent renders.
    const focusOnError = useMemo(() => createDecorator(), []);
    return (
        <Form
            onSubmit={(values) => {
                props.nextFn(values);
            }}
            decorators={[focusOnError]}
            initialValues={props.initialValues}
            keepDirtyOnReinitialize
            render={({
                values,
                errors,
                handleSubmit,
                submitFailed,
                submitErrors,
            }) => {
                useAutoSaveDraft(props.autoSaveCallback, values, errors);

                return (
                    <form onSubmit={handleSubmit}>
                        <TermsOfParticipationFields country={props.country} />
                        {!submitErrors && submitFailed && errors && (
                            <PageErrorComponent error={errors} />
                        )}
                        <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            type="button"
                            onClick={() => props.prevFn(values, errors)}
                            data-testid="previous-page"
                        >
                            &#60; Previous Page
                        </button>
                        <button
                            className="disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded float-right"
                            type="submit"
                            data-testid="next-page"
                            // disabled
                        >
                            Next Page &#62;
                        </button>
                    </form>
                );
            }}
        />
    );
};

TermsOfParticipationOnboardingPage.propTypes = {
    initialValues: T.any,
    country: T.string,
    autoSaveCallback: T.func,
    nextFn: T.func,
    prevFn: T.func,
};

export default TermsOfParticipationOnboardingPage;
